import { useEffect, useMemo, useReducer, useRef } from "react";
import { LRUCache } from "lru-cache";
import { loadHtmlImage } from "utils/loadHtmlImage";
import { buildElementIconCacheKey } from "./buildElementIconCacheKey";
import { cropElementIconsInChunks } from "./cropElementIconsInChunks";
import type { CropElementIconsInChunksItem } from "./cropElementIconsInChunks";
import type { UseGroupIconCache } from "./types";

const MAX_CACHED_PLANS = 5;

type ElementIconCache = Map<string, string>;

const getOrCreatePlanIconCache = (
  cacheByPlan: LRUCache<number, ElementIconCache>,
  floorPlanId: number,
): ElementIconCache => {
  const existing = cacheByPlan.get(floorPlanId);
  if (existing) {
    return existing;
  }
  const created: ElementIconCache = new Map();
  cacheByPlan.set(floorPlanId, created);
  return created;
};

export const useGroupIconCache: UseGroupIconCache = ({ plan }) => {
  const iconCacheByFloorPlanIdRef = useRef<LRUCache<
    number,
    ElementIconCache
  > | null>(null);

  if (!iconCacheByFloorPlanIdRef.current) {
    iconCacheByFloorPlanIdRef.current = new LRUCache({
      max: MAX_CACHED_PLANS,
    });
  }

  const [cacheVersion, bumpCacheVersion] = useReducer(
    (previous: number) => previous + 1,
    0,
  );

  const elementIconCache = plan
    ? getOrCreatePlanIconCache(
        iconCacheByFloorPlanIdRef.current,
        plan.floorPlanId,
      )
    : null;

  const iconByGroupId = useMemo<ReadonlyMap<string, string>>(() => {
    // cacheVersion is read here so that bumpCacheVersion re-derives the map
    // after each chunk lands.
    void cacheVersion;
    const result = new Map<string, string>();
    if (!plan || !elementIconCache) {
      return result;
    }
    for (const groupId of plan.groupedElements.groupIds) {
      const leader = plan.groupedElements.byGroupId[groupId]?.[0];
      if (!leader) {
        continue;
      }
      const cacheKey = buildElementIconCacheKey({
        elementId: leader.id,
        bbox: leader.bbox,
      });
      const dataUrl = elementIconCache.get(cacheKey);
      if (dataUrl) {
        result.set(groupId, dataUrl);
      }
    }
    return result;
  }, [plan, elementIconCache, cacheVersion]);

  useEffect(() => {
    if (!plan || !elementIconCache) {
      return;
    }

    const missingItems: CropElementIconsInChunksItem[] = [];
    for (const groupId of plan.groupedElements.groupIds) {
      const leader = plan.groupedElements.byGroupId[groupId]?.[0];
      if (!leader) {
        continue;
      }
      const cacheKey = buildElementIconCacheKey({
        elementId: leader.id,
        bbox: leader.bbox,
      });

      if (elementIconCache.has(cacheKey)) {
        continue;
      }
      const pageIndex = leader.page ?? 0;
      const pageScaleFactor = plan.pageScaleFactors.get(pageIndex) ?? 1;
      missingItems.push({
        cacheKey,
        bbox: {
          x1: leader.bbox.x1 * pageScaleFactor,
          y1: leader.bbox.y1 * pageScaleFactor,
          x2: leader.bbox.x2 * pageScaleFactor,
          y2: leader.bbox.y2 * pageScaleFactor,
        },
      });
    }

    if (missingItems.length === 0) {
      return;
    }

    const abortController = new AbortController();
    let pendingWork: { cancel: () => void } | null = null;

    loadHtmlImage({ src: plan.planImage })
      .then((sourceImage) => {
        if (abortController.signal.aborted) {
          return;
        }
        pendingWork = cropElementIconsInChunks({
          sourceImage,
          sourceWidthPx: plan.imageWidthPx,
          sourceHeightPx: plan.imageHeightPx,
          items: missingItems,
          onIconCropped: (cacheKey, dataUrl) => {
            elementIconCache.set(cacheKey, dataUrl);
          },
          onChunkComplete: bumpCacheVersion,
        });
      })
      .catch(() => {
        // Image failed to load (CORS or network). Rows render without
        // icons; nothing to recover.
      });

    return () => {
      abortController.abort();
      pendingWork?.cancel();
    };
  }, [plan, elementIconCache]);

  return { iconByGroupId };
};
